import { Button, Combobox, LinkButton, Text } from "@ttc3k/trekker";
import {
  Collapsible,
  ControlledComboboxRoot,
  ControlledInput,
  MapboxMap,
} from "shared/components";
import { Flex } from "styled-system/jsx";
import { MarkerDragEvent } from "react-map-gl";
import { useTranslation } from "react-i18next";
import {
  FieldValues,
  Path,
  PathValue,
  useController,
  useFormContext,
  useWatch,
} from "react-hook-form";
import {
  defaultCoordinatesCA,
  GoogleMapsAddressDetails,
  useGeocoding,
} from "shared/hooks";
import { useEffect, useState } from "react";
import { getAddressString } from "shared/utils";
import { Variants } from "framer-motion";

export interface ControlledAddressAutofillProps<T extends FieldValues> {
  addressLabel?: string;
  addressInputName: Path<T>;
  displayAddressOnly?: boolean;
  addressObjInputName: Path<T>;
  coordsInputName: Path<T>;
  lngInputName: Path<T>;
  latInputName: Path<T>;
}

export const ControlledAddressAutofill = <T extends FieldValues>({
  addressLabel,
  addressInputName,
  displayAddressOnly = false,
  addressObjInputName,
  coordsInputName,
  lngInputName,
  latInputName,
}: ControlledAddressAutofillProps<T>) => {
  const [isManualAddressOpen, setIsManualAddressOpen] = useState(false);
  const { t } = useTranslation("core", { keyPrefix: "SELECT_ADDRESS" });
  const {
    control,
    setValue,
    trigger,
    getFieldState,
    formState: { errors },
  } = useFormContext<T>();
  const { fieldState: addressFieldState } = useController({
    name: addressInputName,
    control,
  });
  const { fieldState: latFieldState } = useController({
    name: latInputName,
    control,
  });
  const { fieldState: lngFieldState } = useController({
    name: lngInputName,
    control,
  });
  const fullAddress = useWatch({ control, name: addressInputName });
  const coordinates = useWatch({ control, name: coordsInputName });
  const addressObj = useWatch({ control, name: addressObjInputName });

  const safeCoord = (v: unknown): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const isValidCoord = (v: unknown) =>
    v !== "" && v !== null && v !== undefined && Number.isFinite(Number(v));
  const isValidLngLat = (lng: unknown, lat: unknown) => {
    const parsedLng = Number(lng);
    const parsedLat = Number(lat);
    return (
      isValidCoord(lng) &&
      isValidCoord(lat) &&
      parsedLng >= -180 &&
      parsedLng <= 180 &&
      parsedLat >= -90 &&
      parsedLat <= 90
    );
  };
  const safeLat = safeCoord(coordinates?.lat);
  const safeLng = safeCoord(coordinates?.lng);
  const coordsAreValid = isValidLngLat(coordinates?.lng, coordinates?.lat);

  const isAddressInvalid = !!(
    addressFieldState.error ||
    latFieldState.error ||
    lngFieldState.error
  );
  const addressHint = addressFieldState.error?.message;

  const handleSelectAddressSuccess = (address: GoogleMapsAddressDetails) => {
    setValue(
      addressObjInputName,
      {
        line1: address.line1,
        line2: address.line2,
        city: address.city,
        subdivision: address.subdivision,
        postalCode: address.postalCode,
        country: address.country,
        location: {
          type: address.location.type,
          coordinates: {
            lng: address.location.coordinates[0],
            lat: address.location.coordinates[1],
          },
        },
      } as PathValue<T, Path<T>>,
      { shouldDirty: true, shouldValidate: true },
    );
    // This is a hack to ensure the inputValue is updated with the correct value
    // On select of an address, Ark's combobox clears the input value so we set
    // the value afterwards
    setTimeout(() => {
      setValue(addressInputName, address.fullAddress as PathValue<T, Path<T>>, {
        shouldDirty: true,
        shouldValidate: true,
      });
      trigger([latInputName, lngInputName]);
    }, 0);
  };

  const handleCoordinateChange = (e: MarkerDragEvent) =>
    setValue(
      coordsInputName,
      {
        lng: e.lngLat.lng,
        lat: e.lngLat.lat,
      } as PathValue<T, Path<T>>,
      { shouldDirty: true, shouldValidate: true },
    );

  const {
    suggestionsMappedToCombobox,
    mapRef,
    marker,
    isMarkerPlaced,
    handleMarkerDragEnd,
    handleSelectAddress,
    handleFlyToAddress,
    getMapCoordinates,
    setMarker,
  } = useGeocoding(
    fullAddress,
    coordinates,
    handleSelectAddressSuccess,
    handleCoordinateChange,
  );

  const showConfirmLocation =
    coordsAreValid &&
    (getFieldState(lngInputName).isDirty ||
      getFieldState(latInputName).isDirty) &&
    !(marker.lng === safeLng && marker.lat === safeLat);

  const handleValidateInput = (fieldName: Path<T>) => {
    trigger([fieldName, addressInputName]);
  };

  const handleUpdateManualAddress = () => {
    setValue(
      addressInputName,
      getAddressString(addressObj) as PathValue<T, Path<T>>,
      { shouldDirty: true },
    );
  };

  const addressObjFieldsInError = errors["fullAddressObj"] ?? [];
  const isHiddenAddressFieldsInError = [
    "line1",
    "postalCode",
    "city",
    "country",
  ].some(val => Object.keys(addressObjFieldsInError).includes(val));

  const handleToggleManualAddress = () => {
    setIsManualAddressOpen(prev => !prev);

    const isClosingManualAddress = isManualAddressOpen;
    /**
     * Adjust the marker to appear on the map when the manual address fields
     * are opened so the user can select their coordinates. Then reset the
     * marker to  either 0 or the selected coordinates if the user closes
     * the manual address
     */
    // If a location already exists, don't reset to the default when switching
    // between manual and auto address fields
    const addrLat = safeCoord(addressObj?.location?.coordinates?.lat);
    const addrLng = safeCoord(addressObj?.location?.coordinates?.lng);
    const addressHasCoordinates = isValidLngLat(
      addressObj?.location?.coordinates?.lng,
      addressObj?.location?.coordinates?.lat,
    );
    const markerPosition =
      isClosingManualAddress || addressHasCoordinates
        ? { lat: addrLat, lng: addrLng }
        : {
            lng: defaultCoordinatesCA[0],
            lat: defaultCoordinatesCA[1],
          };
    setMarker(markerPosition);
  };

  // When the fullAddress is reset, we reset the marker to the default coordinates
  useEffect(() => {
    if (!fullAddress) {
      setMarker({
        lng: defaultCoordinatesCA[0],
        lat: defaultCoordinatesCA[1],
      });
    }
  }, [fullAddress, setMarker]);

  const { collection } = Combobox.useCollection({
    initialItems: suggestionsMappedToCombobox,
  });

  return (
    <Flex flexDir={"column"} gap={"150"}>
      <Flex flexDir={"column"} gap={"25"}>
        <ControlledComboboxRoot
          name={addressInputName}
          collection={collection}
          forceInvalid={isAddressInvalid}
          hint={addressHint}
          onValueChange={handleSelectAddress}
          placeholder={"Start typing in your address..."}
          label={addressLabel ?? t("BUSINESS_ADDRESS")}
        >
          <Combobox.ItemGroup>
            {suggestionsMappedToCombobox.length === 0 && (
              <Combobox.ItemGroupLabel>
                {t("AUTOCOMPLETE_HINT")}
              </Combobox.ItemGroupLabel>
            )}
            {suggestionsMappedToCombobox.map(item => (
              <Combobox.Item key={item.value} item={item} {...item} />
            ))}
          </Combobox.ItemGroup>
        </ControlledComboboxRoot>
        <LinkButton
          width={"max"}
          fontSize={"xs"}
          type={"button"}
          onClick={handleToggleManualAddress}
        >
          {isManualAddressOpen
            ? t("MANUAL_ADDRESS.CLOSE")
            : t("MANUAL_ADDRESS.LABEL")}
        </LinkButton>
      </Flex>

      <Collapsible
        open={isManualAddressOpen}
        additionalVariantProps={
          {
            open: {
              display: "block",
            },
            closed: {
              display: "none",
            },
          } as Variants
        }
      >
        <Flex flexDir={"column"} gap={"150"}>
          <ControlledInput
            name={`${addressObjInputName}.line1`}
            label={t("MANUAL_ADDRESS.LINE_1")}
            onBlurCapture={handleUpdateManualAddress}
          />
          <ControlledInput
            name={`${addressObjInputName}.line2`}
            label={t("MANUAL_ADDRESS.LINE_2")}
            onBlurCapture={handleUpdateManualAddress}
          />
          <Flex
            flexDir={{
              base: "column",
              md: "row",
            }}
            gap={"150"}
          >
            <ControlledInput
              name={`${addressObjInputName}.city`}
              label={t("MANUAL_ADDRESS.CITY")}
              onBlurCapture={handleUpdateManualAddress}
            />
            <ControlledInput
              name={`${addressObjInputName}.subdivision`}
              label={t("MANUAL_ADDRESS.SUBDIVISION")}
              onBlurCapture={handleUpdateManualAddress}
            />
          </Flex>
          <Flex
            flexDir={{
              base: "column",
              md: "row",
            }}
            gap={"150"}
          >
            <ControlledInput
              name={`${addressObjInputName}.postalCode`}
              label={t("MANUAL_ADDRESS.POSTAL_CODE")}
              onBlurCapture={handleUpdateManualAddress}
            />
            <ControlledInput
              name={`${addressObjInputName}.country`}
              label={t("MANUAL_ADDRESS.COUNTRY")}
              onBlurCapture={handleUpdateManualAddress}
            />
          </Flex>
        </Flex>
      </Collapsible>

      {isHiddenAddressFieldsInError && (
        <Text visual={"smallSemiBold"} color={"text.error.dark"}>
          {t("MANUAL_ADDRESS.MISSING_FIELDS")}
        </Text>
      )}

      {!displayAddressOnly && (
        <>
          <Flex
            flexDir={{
              base: "column",
              md: "row",
            }}
            gap={"150"}
            w="full"
          >
            <ControlledInput
              name={latInputName}
              label={t("LATITUDE")}
              placeholder={t("LATITUDE")}
              registerOptions={{ valueAsNumber: true }}
              onBlurCapture={() => handleValidateInput(latInputName)}
            />
            <ControlledInput
              name={lngInputName}
              label={t("LONGITUDE")}
              placeholder={t("LONGITUDE")}
              registerOptions={{ valueAsNumber: true }}
              onBlurCapture={() => handleValidateInput(lngInputName)}
            />
          </Flex>
          <Collapsible open={isMarkerPlaced}>
            <Text visual={"bodyRegular"} color={"text.mid"}>
              {t("MAP_HELPER_TEXT")}
            </Text>
          </Collapsible>
          <Collapsible open={showConfirmLocation}>
            <Button
              type={"button"}
              visual={"unstyled"}
              color={"text.accent.blue.mid"}
              onClick={() => handleFlyToAddress([safeLng, safeLat])}
            >
              {t("CONFIRM_LOCATION")}
            </Button>
          </Collapsible>
          <MapboxMap
            mapRef={mapRef}
            mapCoordinates={getMapCoordinates()}
            markerLng={marker.lng}
            markerLat={marker.lat}
            markerOnDragEnd={handleMarkerDragEnd}
            markerDraggable
          />
        </>
      )}
    </Flex>
  );
};
